/**
 * Phone case stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const phoneCaseStickersPack: ToilePack = {
    slug: 'phone-case-stickers',
    category: 'sticker',
    name: 'Phone case stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'heart', label: 'Heart', subject: 'A small chunky heart with a glossy highlight'},
        {key: 'star', label: 'Star', subject: 'A small chunky star with rounded points'},
        {key: 'flower', label: 'Tiny flower', subject: 'A small five-petal flower with a round centre'},
    ],
    styles: [
        {key: 'holo', label: 'Holographic', style: 'holographic sticker, iridescent rainbow sheen across the surface, glossy', palette: 'Iridescent silver shifting through pink, cyan and gold', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'puffy', label: 'Puffy', style: 'puffy 3D sticker, pillowed shape with soft rounded edges, glossy vinyl surface', palette: 'Bright candy colours with a soft highlight', framing: 'Each sticker isolated on a plain ground, its raised edge showing'},
        {key: 'clear', label: 'Clear', style: 'clear sticker, the design printed on transparent film with a white underlayer, the edge visible', palette: 'Bright flat colour with a visible clear margin', framing: 'Each sticker isolated on a pale ground with the transparent edge showing'},
    ],
    directions: ['as a tiny cactus instead', 'in pastel colours only', 'as a matching pair'],
    directionHint: 'try: a tiny cactus instead',
    seo: {
        title: 'Phone Case Stickers: Heart, Star, Flower Small Sets',
        metaDescription: 'Nine small phone case stickers — a heart, a star, a tiny flower — in holographic, puffy, and clear finishes.',
        h1: 'Phone case stickers: heart, star, and flower in three finishes',
        tagline: 'Nine small stickers, three shapes, three finishes each.',
        intro: 'The small sticker for the case: a heart, a star, a tiny flower — sized for a phone case, not a laptop lid. Rendered holographic, puffy, and clear so the finish matches whatever your case sticker sheet is made of. Ask for a different tiny motif, and the same three finishes carry it.',
        useCases: [
            'decorating a clear or solid phone case',
            'a mini sticker pack for cases and cards',
            'a scrapbook or planner accent sticker',
            'a party favor sticker sheet',
            'a printable small sticker sheet',
        ],
        faq: [
            {q: 'How small are these designs?', a: "They're sized as accent stickers — a fraction of a phone case, not a full-coverage skin — resize before printing for your exact case."},
            {q: "What's the difference between holographic, puffy, and clear?", a: 'Holographic is a rainbow-sheen finish, puffy is a domed 3D look, and clear is a transparent background — match whichever your sticker paper supports.'},
            {q: 'Can I get a different tiny shape, like a moon or a bow?', a: 'Yes — the direction field takes any small motif, rendered in the same three finishes.'},
            {q: 'Will these work on a textured or silicone phone case?', a: 'A smooth or clear case holds a printed sticker best; textured silicone cases may not keep an adhesive sticker in place long-term.'},
            {q: 'Can I print a full sheet for a party favor bag?', a: 'Yes, for personal use; check the license terms before reselling the designs.'},
        ],
    },
}
